import axiosInstance from "Utils/axiosInstance";
import { base_url } from "Zustand/constants";

export interface NewProjectPayload {
  identifier: string;        // uuid or your own slug
  name: string;
  description: string;
  legalentitynodeid: string;
  customer?: string;
}

export interface NewTestSpecificationPayload {
  identifier: string;
  name: string;
  description: string;
  legalentitynodeid: string;
}

export interface UpdateProjectPayload {
  nodeid: string;
  lang: 'en' | 'de' | string;   // adjust if you allow more
  name: string;
  description: string;
  customer?: string;
}

export interface NewTestPlanPayload {
  identifier: string;
  version: string;
  testprojectnodeid: string;
}

export interface AddSpecificationToProjectPayload {
  testspecificationnodeid: string;
}

export interface UpdateTestPlanPayload {
  nodeid: string;
  status?: number;
  legs?: Array<{
    id: string;
    name: string;
    order: number;
    dutCount?: number;
    samplebreakouts?: Array<{
      name: string;
      quantity: number;
      branchQuantities?: Record<string, number>;
    }>;
    branches?: Array<{
      id: string;
      branch: string;
      requirements: Array<{
        nodeid: string;
        name: string;
        category: string;
        order: number;
        testrequirementdata?: Record<string, any>;
      }>;
    }>;
    requirements?: Array<{
      nodeid: string;
      name: string;
      category: string;
      order: number;
      testrequirementdata?: Record<string, any>;
    }>;
  }>;
}

export const getProjectsAPI = async (legalentityNodeId:string) => {
  const res = await axiosInstance.get(
    base_url+"/testprojects?legalEntityNodeId="+legalentityNodeId+"?"
  );
  if (!res) throw new Error('Failed to fetch projects');
  return res.data[0] ? res.data[0].legalentity.hastestproject : [];
};

export const getTestTypesListAPI = async () => {
  const res = await axiosInstance.post(`${base_url}/testvision/testtypeslist`);
  if (!res) throw new Error("Failed to fetch test types");
  return res.data;
};

export const FALLBACK_CUSTOMER_LIST = [
  "A2LA",
  "Acura",
  "Alfa Romeo",
  "ASM",
  "Aston Martin",
  "Audi",
  "BAIC",
  "Bentley",
  "BJEV",
  "BMW",
];

export const getCustomerListAPI = async () => {
  try {
    const res = await axiosInstance.post(`${base_url}/testvision/customerlist`);
    if (!res) throw new Error("Failed to fetch customer list");
    return res.data;
  } catch (error) {
    // Backend should fall back too, but keeping a frontend fallback avoids
    // blocking project creation/editing when TestVision customer lookup fails.
    console.warn("Falling back to hardcoded customer list", error);
    return FALLBACK_CUSTOMER_LIST;
  }
};

export const getTestvisionUserRolesAPI = async (user: string) => {
  const res = await axiosInstance.post(`${base_url}/testvision/userroles`, {
    user,
  });
  return res.data;
};

export const getKpisAPI = async () => {
  const res = await axiosInstance.get(`${base_url}/kpis`);
  return res.data;
};

export const getTestvisionEnvTrsAPI = async (mode?: string) => {
  if (mode !== undefined && mode !== null) {
    let payload = {mode: mode};
    const res = await axiosInstance.post(`${base_url}/testvision/getenvtrs`, payload);
    if (!res) throw new Error("Failed to fetch TestVision TR list");
    return res.data;    
  } else {
    const res = await axiosInstance.post(`${base_url}/testvision/getenvtrs`);
    if (!res) throw new Error("Failed to fetch TestVision TR list");
    return res.data;
  }
};

export const getTestSpecificationsAPI = async (legalentityNodeId: string) => {
  const res = await axiosInstance.get(
    `${base_url}/testspecifications?legalEntityNodeId=${encodeURIComponent(
      legalentityNodeId
    )}`
  );
  if (!res) throw new Error("Failed to fetch test specifications");
  return res.data?.[0]?.legalentity?.hastestspecification ?? [];
};

export const getTestSpecificationContentAPI = async (testSpecificationId: string) => {
  const encodedId = encodeURIComponent(testSpecificationId);
  const res = await axiosInstance.get(`${base_url}/testspecifications/${encodedId}`);
  if (!res) throw new Error("Failed to fetch test specification content");
  return res.data;
};
  

export const getTestPlansAPI = async (projectId: string) => {
  const res = await axiosInstance.get(`${base_url}/testplans?testProjectNodeId=${projectId}`);
  if (!res) throw new Error('Failed to axios.get test plans');
  return res.data ? res.data[0]?.testproject?.hastestplan : [];
};

export const getTestPlanContentAPI = async (planId: string) => {
  const encodedId = encodeURIComponent(planId);
  const res = await axiosInstance.get(`${base_url}/testplans/${encodedId}`);
  if (!res) throw new Error('Failed to fetch test plan content');
  return res.data;
};

export const createTestRequirementAPI = async (payload: any) => {
  const res = await axiosInstance.post(`${base_url}/testrequirements`, payload);
  if (!res) throw new Error('Failed to create test requirement');
  return res.data;
};

export const updateTestRequirementAPI = async (payload: any) => {
  const res = await axiosInstance.put(`${base_url}/testrequirements`, payload);
  if (!res) throw new Error('Failed to update test requirement');
  return res.data;
};

export const deleteTestRequirementAPI = async (nodeid: string) => {
  const res = await axiosInstance.delete(`${base_url}/testrequirements`, {
    data: { nodeid },
  });
  if (res.status !== 200) throw new Error("Failed to delete test requirement");
  return res.data;
};

export const createTestProjectAPI = async (payload: NewProjectPayload) => {
  const res = await axiosInstance.post(base_url+'/testprojects', payload);
  if (res.status !== 201 && res.status !== 200) {
    throw new Error('Failed to create test project');
  }
  // backend returns the full project incl. nodeid
  return res.data;
};

export const createTestSpecificationAPI = async (
  payload: NewTestSpecificationPayload
) => {
  const res = await axiosInstance.post(`${base_url}/testspecifications`, payload);
  if (res.status !== 201 && res.status !== 200) {
    throw new Error("Failed to create test specification");
  }
  return res.data;
};

export const getTestSpecificationByNodeIdAPI = async (nodeid: string) => {
  const encoded = encodeURIComponent(nodeid);
  const res = await axiosInstance.get(`${base_url}/testspecifications/${encoded}`);
  if (res.status !== 200) throw new Error("Failed to fetch test specification");
  return res.data;
};

export async function getTestSpecificationStatusAPI(
  testSpecificationNodeId: string
) {
  const url = `${base_url}/testspecifications/${encodeURIComponent(
    testSpecificationNodeId
  )}/status`;
  const { data } = await axiosInstance.get(url);
  return data;
}

export async function analyzeTestSpecificationAPI(
  testSpecificationNodeId: string
) {
  const url = `${base_url}/testspecifications/${encodeURIComponent(
    testSpecificationNodeId
  )}/analyze`;
  const { data } = await axiosInstance.post(url, {});
  return data;
}

export const updateTestProjectAPI = async (payload: UpdateProjectPayload) => {
  const res = await axiosInstance.put('/testprojects', payload);
  if (res.status !== 200) throw new Error('Failed to update project');
  return res.data;              // updated project from backend
};

export const getTestProjectByNodeIdAPI = async (nodeid: string) => {
  const encoded = encodeURIComponent(nodeid);
  const res = await axiosInstance.get(`/testprojects/${encoded}`);
  if (res.status !== 200) throw new Error('Failed to fetch project');
  return res.data;                // ← backend returns single project wrapper
};

export const createTestPlanAPI = async (payload: NewTestPlanPayload) => {
  const res = await axiosInstance.post('/testplans', payload);
  if (res.status !== 201 && res.status !== 200) {
    throw new Error('Failed to create test plan');
  }
  return res.data;         // returns the wrapped testplan
};

export const addSpecificationToProjectAPI = async (
  projectNodeId: string,
  payload: AddSpecificationToProjectPayload
) => {
  const res = await axiosInstance.post(
    `${base_url}/testprojects/${encodeURIComponent(projectNodeId)}/testspecifications/add`,
    payload
  );
  if (res.status !== 201 && res.status !== 200) {
    throw new Error("Failed to create test plan from test specifications");
  }
  return res.data;
};

export const deleteTestProjectAPI = async (nodeid: string) => {
  const res = await axiosInstance.delete('/testprojects', {
    data: { nodeid },
  });
  if (res.status !== 200) throw new Error('Failed to delete project');
  return res.data; // usually { success: true }
};

export async function getProjectStatusAPI(projectNodeId: string) {
  const url = `${base_url}/testprojects/${encodeURIComponent(projectNodeId)}/status`;
  const { data } = await axiosInstance.get(url);
  return data; // {status, startedat, finishedat, progress, message}
}

export async function analyzeProjectAPI(projectNodeId: string) {
  const url = `${base_url}/testprojects/${encodeURIComponent(projectNodeId)}/analyze`;
  const { data } = await axiosInstance.post(url, {}); // body not required per spec
  return data;
}


// GET a single testplan
export async function getTestPlanByNodeIdAPI(nodeid: string) {
  const url = `${base_url}/testplans/${encodeURIComponent(nodeid)}`;
  const res = await axiosInstance.get(url);
  return res.data; // can be { testplan: {...} } or plain – component handles both
}

// PUT update (e.g., status change)
export async function updateTestPlanAPI(payload: UpdateTestPlanPayload) {
  const res = await axiosInstance.put(`${base_url}/testplans`, payload);
  return res.data;
}

export async function createTestPlanSpecInTestvisionAPI(
  testPlanNodeId: string,
  payload: { user: string }
) {
  const url = `${base_url}/testplans/${encodeURIComponent(
    testPlanNodeId
  )}/testvision/spec`;
  const res = await axiosInstance.post(url, payload);
  return res.data;
}

export async function createTestPlanFlowInTestvisionAPI(
  testPlanNodeId: string,
  payload: { user: string; trid: number | string; name: string }
) {
  const url = `${base_url}/testplans/${encodeURIComponent(
    testPlanNodeId
  )}/testvision/flow`;
  const res = await axiosInstance.post(url, payload);
  return res.data;
}

export async function createTestReportFormFromTestvisionAPI(payload: {
  trid: number | string;
}) {
  const res = await axiosInstance.post(
    `${base_url}/testreports/testvision`,
    payload
  );
  return res.data;
}

// POST clone
export async function cloneTestPlanAPI(nodeid: string, body: {
  phase: string;
  version: string;
  istestplanofnodeid: string;
}) {
  const url = `${base_url}/testplans/${encodeURIComponent(nodeid)}/clone`;
  const res = await axiosInstance.post(url, body);
  return res.data;
}
